import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Group } from '@/api/Dara/Groups'
import { oneHourInMS } from '@/composable/useDatesAndTimes'
import { groupKeys } from '@/composable/queries/keys'
import { useInvalidateQueries as useInvalidateStudentQueries } from '@/composable/queries/Dara/students'

interface CreateData {
  formData: object;
}

interface CreateSubGroupData {
  groupId: number;
  formData: object;
}

interface UpdateData {
  groupId: number;
  formData: object;
}

interface DeleteData {
  groupId: number;
}

interface ReorderData {
  formData: {
    group_ids: number[];
  }
}

interface CopyGroupsData {
  formData: {
    groups_to_copy: number[];
    groups_to_include_sub_groups: number[];
  }
}

export const useInvalidateGroupsQueries = () => {
  const queryClient = useQueryClient()

  const invalidateAcademicYearGroupsQuery = (academicYearId: number) => {
    queryClient.invalidateQueries({
      queryKey: groupKeys.list(ref(academicYearId)),
    })
  }

  return {
    invalidateAcademicYearGroupsQuery,
  }
}

interface MoveStudentsData {
  formData: {
    year_group_links: object[];
  }
}

export const useGroupQuery = (
  academicYearId: Ref<number>,
  enabled: Ref<boolean>,
) =>
  useQuery({
    queryKey: groupKeys.list(academicYearId),
    queryFn: () => Group.getByAcademicYear(academicYearId.value),
    staleTime: oneHourInMS,
    enabled,
  })

export const useCreateGroup = (
  academicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (createData: CreateData) => Group.createGroup(academicYearId, createData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(academicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useCreateSubGroup = (
  academicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (createData: CreateSubGroupData) => Group.createSubGroup(createData.groupId, createData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(academicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useUpdateGroup = (
  academicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (updateData: UpdateData) => Group.updateGroup(updateData.groupId, updateData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(academicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useDeleteGroup = (
  academicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (deleteData: DeleteData) => Group.deleteGroup(deleteData.groupId),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(academicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useReorderGroups = (
  academicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (reorderData: ReorderData) => Group.reorderGroups(academicYearId, reorderData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(academicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useCopyGroups = (
  previousAcademicYearId: number,
  currentAcademicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateOrganisationStudentCreateQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (copyData: CopyGroupsData) => Group.copyGroups(previousAcademicYearId, copyData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(currentAcademicYearId)
      invalidateOrganisationStudentCreateQuery()
    },
  })
}

export const useMoveStudents = (
  previousAcademicYearId: number,
  currentAcademicYearId: number,
) => {
  const { invalidateAcademicYearGroupsQuery  } = useInvalidateGroupsQueries()
  const { invalidateStudentsQuery  } = useInvalidateStudentQueries()

  return useMutation({
    mutationFn: (moveData: MoveStudentsData) => Group.moveStudents(previousAcademicYearId, moveData.formData),
    onSuccess: () => {
      invalidateAcademicYearGroupsQuery(currentAcademicYearId)
      invalidateStudentsQuery()
    },
  })
}
